import { useState, useEffect, useCallback } from 'react';
import { getBookings, checkIn, checkOut } from '../lib/api';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { LogIn, LogOut, Search, Clock, Undo2 } from 'lucide-react';
import { toast } from 'sonner';

export default function CheckInOut() {
  const [pendingCheckIns, setPendingCheckIns] = useState([]);
  const [pendingCheckOuts, setPendingCheckOuts] = useState([]);
  const [recentCheckOuts, setRecentCheckOuts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');

  const loadData = useCallback(async () => {
    try {
      const [checkInsRes, checkOutsRes, recentRes] = await Promise.all([
        getBookings('confirmed'),
        getBookings('checked_in'),
        getBookings('checked_out'),
      ]);
      setPendingCheckIns(checkInsRes.data);
      setPendingCheckOuts(checkOutsRes.data);
      setRecentCheckOuts(recentRes.data.slice(0, 20));
    } catch {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const handleCheckIn = async (booking) => {
    if (!window.confirm(`Check in ${booking.guest?.name} to Room ${booking.room?.room_number}?`)) return;
    try {
      await checkIn(booking.id);
      toast.success(`${booking.guest?.name} checked in!`);
      loadData();
    } catch (error) { toast.error(error.response?.data?.detail || 'Check-in failed'); }
  };

  const handleCheckOut = async (booking) => {
    if (!window.confirm(`Check out ${booking.guest?.name} from Room ${booking.room?.room_number}?`)) return;
    try {
      await checkOut(booking.id);
      toast.success(`${booking.guest?.name} checked out!`);
      loadData();
    } catch (error) { toast.error(error.response?.data?.detail || 'Check-out failed'); }
  };

  const handleUndoCheckIn = async (booking) => {
    if (!window.confirm(`Undo check-in for ${booking.guest?.name}? This will revert to confirmed status.`)) return;
    try {
      await api.post(`/bookings/${booking.id}/undo-check-in`);
      toast.success('Check-in undone');
      loadData();
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed'); }
  };

  const handleUndoCheckOut = async (booking) => {
    if (!window.confirm(`Undo check-out for ${booking.guest?.name}? Guest will be marked as checked-in again.`)) return;
    try {
      await api.post(`/bookings/${booking.id}/undo-check-out`);
      toast.success('Check-out undone — guest is checked back in');
      loadData();
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed'); }
  };

  const filterBookings = (bookings) => {
    const q = searchQuery.toLowerCase();
    return bookings.filter(b => (b.guest?.name?.toLowerCase() || '').includes(q) || (b.room?.room_number?.toLowerCase() || '').includes(q));
  };

  const fmt = (a) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR' }).format(a);

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-sage-700"></div></div>;

  const BookingRow = ({ booking, index, actions }) => (
    <TableRow key={booking.id} className="table-row-hover animate-fade-in" style={{ animationDelay: `${index * 0.03}s` }} data-testid={`booking-row-${booking.id}`}>
      <TableCell>
        <p className="font-medium text-stone-900">{booking.guest?.name}</p>
        <p className="text-sm text-stone-500">{booking.guest?.phone || booking.guest?.email}</p>
      </TableCell>
      <TableCell>
        <p className="font-medium text-stone-900">Room {booking.room?.room_number}</p>
        <p className="text-sm text-stone-500 capitalize">{booking.room?.room_type}</p>
      </TableCell>
      <TableCell className="text-stone-600">{booking.check_in_date}</TableCell>
      <TableCell className="text-stone-600">{booking.check_out_date}</TableCell>
      <TableCell className="font-medium text-stone-900">{fmt(booking.total_amount)}</TableCell>
      <TableCell className="text-right"><div className="flex gap-1 justify-end">{actions}</div></TableCell>
    </TableRow>
  );

  const EmptyState = ({ text }) => (
    <div className="p-12 text-center"><Clock className="w-12 h-12 mx-auto mb-3 text-stone-300" /><p className="text-stone-500">{text}</p></div>
  );

  const TableHead_ = () => (
    <TableHeader><TableRow className="bg-stone-50 hover:bg-stone-50">
      <TableHead className="font-semibold text-stone-700">Guest</TableHead>
      <TableHead className="font-semibold text-stone-700">Room</TableHead>
      <TableHead className="font-semibold text-stone-700">Check-in</TableHead>
      <TableHead className="font-semibold text-stone-700">Check-out</TableHead>
      <TableHead className="font-semibold text-stone-700">Total</TableHead>
      <TableHead className="font-semibold text-stone-700 text-right">Actions</TableHead>
    </TableRow></TableHeader>
  );

  return (
    <div data-testid="checkinout-page">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-stone-900 font-['Manrope'] tracking-tight">Check In / Check Out</h1>
        <p className="text-stone-500 mt-1">Manage guest arrivals and departures</p>
      </div>

      <div className="flex gap-3 mb-6">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
          <Input placeholder="Search guest or room..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="pl-9 border-stone-300" data-testid="search-checkinout-input" />
        </div>
      </div>

      <Tabs defaultValue="checkin" className="w-full">
        <TabsList className="bg-stone-100 p-1 mb-6">
          <TabsTrigger value="checkin" className="data-[state=active]:bg-white data-[state=active]:text-sage-700 px-6" data-testid="tab-checkin">
            <LogIn className="w-4 h-4 mr-2" />Check-ins ({pendingCheckIns.length})
          </TabsTrigger>
          <TabsTrigger value="checkout" className="data-[state=active]:bg-white data-[state=active]:text-sage-700 px-6" data-testid="tab-checkout">
            <LogOut className="w-4 h-4 mr-2" />Check-outs ({pendingCheckOuts.length})
          </TabsTrigger>
          <TabsTrigger value="recent" className="data-[state=active]:bg-white data-[state=active]:text-sage-700 px-6" data-testid="tab-recent">
            <Undo2 className="w-4 h-4 mr-2" />Recent ({recentCheckOuts.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="checkin">
          <div className="bg-white border border-stone-200 rounded-md overflow-hidden">
            {filterBookings(pendingCheckIns).length === 0 ? <EmptyState text="No pending check-ins" /> : (
              <Table><TableHead_ /><TableBody>
                {filterBookings(pendingCheckIns).map((b, i) => (
                  <BookingRow key={b.id} booking={b} index={i} actions={
                    <Button onClick={() => handleCheckIn(b)} className="bg-emerald-700 hover:bg-emerald-800 text-white rounded-sm" data-testid={`do-checkin-${b.id}`}>
                      <LogIn className="w-4 h-4 mr-2" />Check In
                    </Button>
                  } />
                ))}
              </TableBody></Table>
            )}
          </div>
        </TabsContent>

        <TabsContent value="checkout">
          <div className="bg-white border border-stone-200 rounded-md overflow-hidden">
            {filterBookings(pendingCheckOuts).length === 0 ? <EmptyState text="No pending check-outs" /> : (
              <Table><TableHead_ /><TableBody>
                {filterBookings(pendingCheckOuts).map((b, i) => (
                  <BookingRow key={b.id} booking={b} index={i} actions={<>
                    <Button onClick={() => handleUndoCheckIn(b)} variant="outline" size="sm" className="text-amber-700 border-amber-200 hover:bg-amber-50" data-testid={`undo-checkin-${b.id}`}>
                      <Undo2 className="w-3.5 h-3.5 mr-1" />Undo
                    </Button>
                    <Button onClick={() => handleCheckOut(b)} className="bg-sage-700 hover:bg-sage-800 text-white rounded-sm" data-testid={`do-checkout-${b.id}`}>
                      <LogOut className="w-4 h-4 mr-2" />Check Out
                    </Button>
                  </>} />
                ))}
              </TableBody></Table>
            )}
          </div>
        </TabsContent>

        <TabsContent value="recent">
          <div className="bg-white border border-stone-200 rounded-md overflow-hidden">
            {filterBookings(recentCheckOuts).length === 0 ? <EmptyState text="No recent check-outs" /> : (
              <Table><TableHead_ /><TableBody>
                {filterBookings(recentCheckOuts).map((b, i) => (
                  <BookingRow key={b.id} booking={b} index={i} actions={
                    <Button onClick={() => handleUndoCheckOut(b)} variant="outline" size="sm" className="text-amber-700 border-amber-200 hover:bg-amber-50" data-testid={`undo-checkout-${b.id}`}>
                      <Undo2 className="w-3.5 h-3.5 mr-1" />Undo Check-out
                    </Button>
                  } />
                ))}
              </TableBody></Table>
            )}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
